import { produce } from 'immer';
import { type ReactNode, createContext, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { REDIRECT } from '../constants/RoutePaths';
import * as CompaniesService from '../services/companies';
import { getRouteFrom } from '../utils/getRoutes';
import Logger from '../utils/logger';
import { ByName } from '../utils/sortObjects';
export interface ICompaniesContextData {
  addCompany: (company: CompaniesService.IAddCompanyDTO) => Promise<void>;
  companies: CompaniesService.ICompanyDTO[];
  deleteCompany: (id: number) => Promise<void>;
  selectCompany: (
    company: CompaniesService.ICompanyDTO | null
  ) => Promise<void>;
  selectListOptionStyle: (optionStyle: IListStyleOptions) => Promise<void>;
  selectCompanyByID: (companyId: string) => Promise<void>;
  selectedCompany: CompaniesService.ICompanyDTO | null;
  selectedListOptionStyle: string | null;
  syncCompanies: (
    selectCompanyId?: string
  ) => Promise<CompaniesService.ICompanyDTO[]>;
  updateCompany: (data: CompaniesService.IUpdateCompanyDTO) => Promise<void>;
  deleteLogoImage: (companyId: number) => Promise<void>;
}

interface ICompaniesProps {
  children: ReactNode;
}

interface IListStyleOptions {
  optionStyle: string;
}

export const CompaniesContext = createContext<ICompaniesContextData>(
  {} as ICompaniesContextData
);

export const CompaniesProvider = ({
  children,
}: ICompaniesProps): JSX.Element => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CompaniesService.ICompanyDTO[]>(
    []
  );
  const [selectedCompany, setSelectedCompany] =
    useState<CompaniesService.ICompanyDTO | null>(null);
  const [selectedListOptionStyle, setSelectedListOptionStyle] =
    useState<string>('list');

  const addCompany = useCallback(
    async (data: CompaniesService.IAddCompanyDTO) => {
      try {
        const result = await CompaniesService.addCompany(data);

        let index = companies.findIndex((company) => {
          return ByName(result, company) < 0;
        });
        if (index < 0) {
          index = companies.length;
        }

        setCompanies(
          produce((draft) => {
            draft.splice(index, 0, result);
          })
        );
      } catch (error) {
        Logger.debug('error: ', error);
      }
    },
    [companies]
  );

  const updateCompany = useCallback(
    async (data: CompaniesService.IUpdateCompanyDTO) => {
      try {
        const result = await CompaniesService.updateCompany(data);

        let index = companies.findIndex(({ id }) => {
          return id === result.id;
        });
        if (index < 0) {
          index = companies.length;
        }

        setCompanies(
          produce((draft) => {
            draft.splice(index, 1, result);
          })
        );
        setSelectedCompany(result);
      } catch (error) {
        Logger.debug('error: ', error);
        throw error;
      }
    },
    [companies]
  );

  const deleteCompany = useCallback(
    async (id: number) => {
      try {
        await CompaniesService.deleteCompany(id);

        const index = companies.findIndex((company) => {
          return company.id === id;
        });

        if (selectedCompany && id === selectedCompany.id) {
          setSelectedCompany(null);
        }
        setCompanies(
          produce((draft) => {
            draft.splice(index, 1);
          })
        );
      } catch (error) {
        Logger.debug('error: ', error);
      }
    },
    [companies, selectedCompany]
  );

  const refreshCompanies = useCallback(
    async (companyId: number): Promise<CompaniesService.ICompanyDTO[]> => {
      const result = await CompaniesService.getCompanies({
        page: 1,
        limit: 12,
        filter: '',
        all: true,
      });
      if (!result.data.length) {
        setSelectedCompany(null);
        setCompanies(result.data);
        return result.data;
      }

      if (companyId > 0) {
        const newCompany =
          result.data.find(({ id }) => companyId === id) ?? null;

        if (!newCompany) {
          navigate(getRouteFrom(REDIRECT));
          setCompanies(result.data);
          throw Error('Not Found');
        }

        setSelectedCompany(newCompany);
      } else if (result.data.length === 1) {
        setSelectedCompany(result.data[0]);
      }

      setCompanies(result.data);
      return result.data;
    },
    []
  );

  const selectCompany = useCallback(
    async (company: CompaniesService.ICompanyDTO | null) => {
      setSelectedCompany(company);
    },
    []
  );

  const selectListOptionStyle = useCallback(
    async (optionStyle: IListStyleOptions) => {
      setSelectedListOptionStyle(optionStyle.optionStyle);
    },
    []
  );

  const selectCompanyByID = useCallback(
    async (companyId: string) => {
      try {
        const companyIdFromPath = Number(companyId);
        if (!companies.length) {
          await refreshCompanies(companyIdFromPath);
          return;
        }

        const newCompany =
          companies.find(({ id }) => companyIdFromPath === id) ?? null;
        setSelectedCompany(newCompany);

        if (!newCompany) {
          navigate(getRouteFrom(REDIRECT));
          throw Error('Not Found');
        }
      } catch (error) {
        Logger.debug('error: ', error);
      }
    },
    [companies, refreshCompanies, navigate]
  );

  const syncCompanies = useCallback(
    async (selectCompanyId = ''): Promise<CompaniesService.ICompanyDTO[]> => {
      const companyIdFromPath = Number(selectCompanyId);

      const idToSelect = (companyIdFromPath || selectedCompany?.id) ?? NaN;

      try {
        return await refreshCompanies(idToSelect);
      } catch (err) {
        Logger.debug('err: ', err);
        return [];
      }
    },
    [refreshCompanies, selectedCompany?.id]
  );

  const deleteLogoImage = useCallback(
    async (companyId: number) => {
      try {
        const result = await CompaniesService.deleteLogoImage(companyId);

        const index = companies.findIndex((company) => {
          return company.id === companyId;
        });

        setCompanies(
          produce((draft) => {
            draft.splice(index, 1, result);
          })
        );
        setSelectedCompany(result);
      } catch (error) {
        Logger.debug('error: ', error);
      }
    },
    [companies]
  );

  return (
    <CompaniesContext.Provider
      value={{
        addCompany,
        companies,
        deleteCompany,
        selectCompany,
        selectListOptionStyle,
        selectCompanyByID,
        selectedCompany,
        selectedListOptionStyle,
        syncCompanies,
        updateCompany,
        deleteLogoImage,
      }}
    >
      {children}
    </CompaniesContext.Provider>
  );
};
